import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  AlertTriangle,
  Save,
  X,
  Info,
  ExternalLink,
} from 'lucide-react';
import { DUREE_PRESETS, normalizeList } from '../utils/creneauConfig';
import { analyzeTableReservationHoraires } from '../utils/tableReservationHoraires';

const buildServicesState = (source) => ({
  livraison: source?.livraison ?? false,
  livraisonAsap: source?.livraisonAsap ?? true,
  livraisonCreneaux: source?.livraisonCreneaux ?? false,
  clickAndCollect: source?.clickAndCollect ?? false,
  paiementEnLigne: source?.paiementEnLigne ?? false,
  paiementSurPlace: source?.paiementSurPlace ?? false,
  priseRDV: source?.priseRDV ?? false,
  reservationTable: source?.reservationTable ?? false,
  priseCreneaux: source?.priseCreneaux ?? false,
});

export const buildReservationConfigState = (source) => {
  const durees = normalizeList(source?.dureesAutoriseesMinutes);
  return {
    pasGrilleMinutes: source?.pasGrilleMinutes ?? 15,
    dureesAutoriseesMinutes: durees.length ? durees : [60, 90, 120],
    dureeRDVMinutes: source?.dureeRDVMinutes ?? 60,
    dureeTableMinutes: source?.dureeTableMinutes ?? 120,
    maxRDVParJour: source?.maxRDVParJour ?? 20,
    maxTablesParJour: source?.maxTablesParJour ?? 50,
    delaiAnnulationHeures: source?.delaiAnnulationHeures ?? 24,
    confirmationAutomatique: source?.confirmationAutomatique ?? true,
    rappelRDVHeures: source?.rappelRDVHeures ?? 2,
    arrhesTableActives: source?.arrhesTableActives ?? false,
    arrhesTableMode: source?.arrhesTableMode ?? 'FIXE',
    arrhesTableMontant: source?.arrhesTableMontant ?? 0,
    arrhesTableExemptCompteVerifie: source?.arrhesTableExemptCompteVerifie ?? false,
    arrhesTableSeuilExemptionCommandes: source?.arrhesTableSeuilExemptionCommandes ?? null,
  };
};

const RESERVATION_SERVICE_KEYS = ['priseRDV', 'reservationTable', 'priseCreneaux'];

/** Vrais services affichés dans « Services disponibles » (Lot 1 UI — hors moyens de paiement). */
const SERVICE_GRID_KEYS = [
  'livraison',
  'clickAndCollect',
  'priseRDV',
  'reservationTable',
  'priseCreneaux',
];

const ConfigField = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
  </div>
);

const ReservationConfigCard = ({ icon, title, children }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-5">
    <div className="mb-4 flex items-center gap-2 border-b border-gray-200 pb-3">
      <span className="text-xl" aria-hidden>{icon}</span>
      <h5 className="font-semibold text-gray-900">{title}</h5>
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
  </div>
);

const inputClassName =
  'w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500';

const ServicesConfigurationPanel = ({
  commercant,
  onUpdate,
  loading = false,
  onNavigateToTab,
  onNavigateToSetupSection,
  /** Slot Lot 1 : bloc « Moyens de paiement » (globals + matrice), sous Services. */
  moyensDePaiementSlot,
}) => {
  const [services, setServices] = useState(() => buildServicesState(commercant));
  const [config, setConfig] = useState(() => buildReservationConfigState(commercant));
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const baseServices = buildServicesState(commercant);
    const autoEnableKeys = (getServicesByCategorie(commercant?.categorie)?.required || []).filter(
      (key) => RESERVATION_SERVICE_KEYS.includes(key),
    );

    autoEnableKeys.forEach((key) => {
      if (!baseServices[key]) baseServices[key] = true;
    });

    setServices(baseServices);
    setConfig(buildReservationConfigState(commercant));
    setHasChanges(false);
  }, [commercant]);

  const getServicesByCategorie = (categorie) => {
    const configs = {
      RESTAURANT: {
        required: [],
        recommended: ['reservationTable', 'livraison', 'clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: [],
      },
      CAFE: {
        required: [],
        recommended: ['paiementEnLigne', 'reservationTable', 'clickAndCollect', 'paiementSurPlace'],
        optional: ['livraison'],
      },
      PIZZERIA: {
        required: [],
        recommended: ['livraison', 'paiementEnLigne', 'clickAndCollect', 'paiementSurPlace'],
        optional: ['reservationTable'],
      },
      FAST_FOOD: {
        required: [],
        recommended: ['livraison', 'paiementEnLigne', 'clickAndCollect', 'paiementSurPlace'],
        optional: [],
      },
      SERVICE: {
        required: [],
        recommended: ['priseCreneaux', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect'],
      },
      CONSULTANT: {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect'],
      },
      COMPTABILITE: {
        required: [],
        recommended: ['priseRDV', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['clickAndCollect'],
      },
      PHARMACIE: {
        required: [],
        recommended: ['clickAndCollect', 'paiementEnLigne', 'paiementSurPlace'],
        optional: ['livraison', 'priseRDV'],
      },
      AUTRE: {
        required: [],
        recommended: ['paiementEnLigne', 'paiementSurPlace'],
        optional: ['livraison', 'clickAndCollect', 'priseRDV', 'priseCreneaux'],
      },
      DEFAULT: {
        required: [],
        recommended: ['paiementEnLigne', 'paiementSurPlace'],
        optional: ['livraison', 'clickAndCollect', 'priseRDV'],
      },
    };

    return configs[categorie] || configs.DEFAULT;
  };

  const serviceConfig = getServicesByCategorie(commercant?.categorie);

  const getServiceIcon = (serviceKey) => {
    const icons = {
      livraison: '🚚',
      clickAndCollect: '📦',
      paiementEnLigne: '💳',
      paiementSurPlace: '💰',
      priseRDV: '📅',
      reservationTable: '🍽️',
      priseCreneaux: '⏰',
    };
    return icons[serviceKey] || '⚙️';
  };

  const getServiceLabel = (serviceKey) => {
    const labels = {
      livraison: 'Service de livraison',
      clickAndCollect: 'Click & Collect',
      paiementEnLigne: 'Paiement en ligne (CB)',
      paiementSurPlace: 'Paiement sur place',
      priseRDV: 'Prise de rendez-vous',
      reservationTable: 'Réservation de table',
      priseCreneaux: 'Prise de créneaux',
    };
    return labels[serviceKey] || serviceKey;
  };

  const getServiceDescription = (serviceKey) => {
    const descriptions = {
      livraison: 'Livraison à domicile',
      clickAndCollect: 'Retrait sur place',
      paiementEnLigne: 'Accepter le paiement en ligne',
      paiementSurPlace: 'Paiement en espèces ou CB sur place',
      priseRDV: 'Permettre aux clients de prendre rendez-vous',
      reservationTable: 'Réservation de table en ligne',
      priseCreneaux: 'Prise de créneaux pour services',
    };
    return descriptions[serviceKey] || '';
  };

  const handleServiceToggle = (serviceKey) => {
    const next = !services[serviceKey];
    const newServices = { ...services, [serviceKey]: next };
    if (serviceKey === 'priseCreneaux') {
      if (next) newServices.priseRDV = true;
    }
    if (serviceKey === 'livraison' && next) {
      newServices.livraisonAsap = true;
      newServices.livraisonCreneaux = false;
    }
    setServices(newServices);
    setHasChanges(true);
  };

  const handleConfigChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const toggleDureeCreneau = (minutes) => {
    setConfig((prev) => {
      const set = new Set(prev.dureesAutoriseesMinutes);
      if (set.has(minutes)) set.delete(minutes);
      else set.add(minutes);
      const next = [...set].sort((a, b) => a - b);
      return { ...prev, dureesAutoriseesMinutes: next.length ? next : [60] };
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate({
      ...services,
      ...config,
      moduleCreneaux: Boolean(services.priseCreneaux),
      priseCreneaux: services.priseCreneaux,
    });
    setHasChanges(false);
  };

  const isServiceRequired = (serviceKey) => serviceConfig.required.includes(serviceKey);
  const isServiceRecommended = (serviceKey) => serviceConfig.recommended.includes(serviceKey);

  const getValidationErrors = () => {
    const errors = [];
    const coreServices = ['livraison', 'clickAndCollect', ...RESERVATION_SERVICE_KEYS];
    const hasCoreService = coreServices.some((service) => services[service]);

    if (!hasCoreService) {
      const labels = coreServices
        .filter((service) => typeof services[service] !== 'undefined')
        .map(getServiceLabel)
        .join(', ');
      errors.push(`Activez au moins un service principal (${labels}).`);
    }

    if (services.livraison && !services.livraisonAsap && !services.livraisonCreneaux) {
      errors.push('Si la livraison locale est activée, choisissez « Dès que possible » et/ou « Livraison sur créneaux ».');
    }

    serviceConfig.required.forEach((requiredService) => {
      if (!services[requiredService]) {
        errors.push(`Le service "${getServiceLabel(requiredService)}" est obligatoire pour cette catégorie`);
      }
    });

    return errors;
  };

  const validationErrors = getValidationErrors();
  const activeReservationCount = RESERVATION_SERVICE_KEYS.filter((key) => services[key]).length;
  const showReservationConfig = activeReservationCount > 0;
  const horairesAnalysis = useMemo(
    () => analyzeTableReservationHoraires(commercant?.horaires),
    [commercant?.horaires],
  );
  const sharedFieldHint =
    activeReservationCount > 1
      ? 'Cette valeur s\'applique à tous vos services de réservation activés.'
      : undefined;

  const navigateToTab = (tabId) => {
    if (onNavigateToTab) onNavigateToTab(tabId);
  };

  const handleCancelChanges = () => {
    setServices(buildServicesState(commercant));
    setConfig(buildReservationConfigState(commercant));
    setHasChanges(false);
  };

  return (
    <>
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="rounded-lg bg-blue-100 p-2">
            <Calendar className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Services proposés</h3>
            <p className="text-sm text-gray-500">
              Configurez les services disponibles pour votre commerce
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Catégorie:</span>
          <span className="rounded-full bg-blue-100 px-2 py-1 text-sm font-medium text-blue-800">
            {commercant?.categorie || 'Non définie'}
          </span>
        </div>
      </div>

      {validationErrors.length > 0 && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
            <div>
              <h4 className="text-sm font-medium text-red-800">Configuration incomplète</h4>
              <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-red-700">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h4 className="font-medium text-gray-900">Services disponibles</h4>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {SERVICE_GRID_KEYS.map((serviceKey) => {
            const isRequired = isServiceRequired(serviceKey);
            const isRecommended = isServiceRecommended(serviceKey);

            return (
              <div
                key={serviceKey}
                className={`rounded-lg border-2 p-4 transition-all ${
                  services[serviceKey]
                    ? 'border-green-200 bg-green-50'
                    : 'border-red-100 bg-white hover:border-red-200'
                } ${isRequired ? 'ring-2 ring-red-200' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getServiceIcon(serviceKey)}</span>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h5 className="font-medium text-gray-900">{getServiceLabel(serviceKey)}</h5>
                        {isRequired && (
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                            Obligatoire
                          </span>
                        )}
                        {isRecommended && !isRequired && (
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                            Recommandé
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-500">{getServiceDescription(serviceKey)}</p>
                    </div>
                  </div>

                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={services[serviceKey]}
                      onChange={() => handleServiceToggle(serviceKey)}
                      className="peer sr-only"
                      aria-label={getServiceLabel(serviceKey)}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-red-500 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-green-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-200" />
                  </label>
                </div>
                {serviceKey === 'livraison' && services.livraison && onNavigateToTab && (
                  <div className="mt-3 border-t border-green-200 pt-3">
                    <button
                      type="button"
                      onClick={() => navigateToTab('livraison')}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:text-indigo-900"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Configurer la livraison
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showReservationConfig && (
        <div className="mt-8 space-y-4 border-t border-gray-200 pt-6">
          <h4 className="font-medium text-gray-900">Réglages par service</h4>

          {activeReservationCount > 1 && (
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
              <p className="text-sm text-blue-900">
                Chaque service activé a ses propres réglages ci-dessous. Les délais d&apos;annulation et
                confirmations sont partagés entre vos services de réservation.
              </p>
            </div>
          )}

          <div className="space-y-4">
            {services.priseRDV && (
              <ReservationConfigCard icon="📅" title="Prise de rendez-vous">
                <ConfigField
                  label="Durée d'un rendez-vous (minutes)"
                  hint="Temps moyen d'un créneau, par exemple 60 minutes."
                >
                  <input
                    type="number"
                    value={config.dureeRDVMinutes}
                    onChange={(e) => handleConfigChange('dureeRDVMinutes', parseInt(e.target.value, 10) || 60)}
                    className={inputClassName}
                    min="15"
                    max="480"
                    step="15"
                  />
                </ConfigField>

                <ConfigField
                  label="Délai minimum avant annulation (heures)"
                  hint={sharedFieldHint || 'Ex. 24 h avant le rendez-vous.'}
                >
                  <input
                    type="number"
                    value={config.delaiAnnulationHeures}
                    onChange={(e) => handleConfigChange('delaiAnnulationHeures', parseInt(e.target.value, 10) || 24)}
                    className={inputClassName}
                    min="1"
                    max="168"
                  />
                </ConfigField>

                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.confirmationAutomatique}
                      onChange={(e) => handleConfigChange('confirmationAutomatique', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Confirmer automatiquement les rendez-vous
                    </span>
                  </label>
                  {sharedFieldHint && (
                    <p className="mt-1 text-xs text-gray-500">{sharedFieldHint}</p>
                  )}
                </div>
              </ReservationConfigCard>
            )}

            {services.reservationTable && (
              <ReservationConfigCard icon="🍽️" title="Réservation de table">
                <div
                  className={`md:col-span-2 rounded-lg border p-4 ${
                    !horairesAnalysis.hasAny
                      ? 'border-rose-100 bg-rose-50/70'
                      : horairesAnalysis.hasMultiShift
                        ? 'border-green-100 bg-green-50/70'
                        : 'border-amber-100 bg-amber-50/70'
                  }`}
                >
                  <p
                    className={`text-sm font-medium ${
                      !horairesAnalysis.hasAny
                        ? 'text-rose-900'
                        : horairesAnalysis.hasMultiShift
                          ? 'text-green-900'
                          : 'text-amber-900'
                    }`}
                  >
                    Créneaux visibles sur le site
                  </p>
                  {!horairesAnalysis.hasAny ? (
                    <p className="mt-1 text-sm text-rose-800">
                      Aucun horaire d&apos;ouverture actif. Les clients ne verront aucun créneau tant
                      que vous n&apos;avez pas configuré vos plages horaires.
                    </p>
                  ) : horairesAnalysis.hasMultiShift ? (
                    <p className="mt-1 text-sm text-green-800">
                      Vos créneaux sont générés depuis vos horaires d&apos;ouverture
                      {horairesAnalysis.multiShiftDayCount > 0
                        ? ` (${horairesAnalysis.multiShiftDayCount} jour${horairesAnalysis.multiShiftDayCount > 1 ? 's' : ''} avec plusieurs plages — ex. midi et soir).`
                        : '.'}
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-amber-800">
                      Vous avez une seule plage horaire par jour ouvert. Pour proposer le service du
                      soir en plus du midi, ajoutez une 2<sup>e</sup> plage sur les jours concernés.
                    </p>
                  )}
                  {horairesAnalysis.summaryLines.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
                      {horairesAnalysis.summaryLines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-3 flex flex-wrap gap-4">
                    {onNavigateToSetupSection && (
                      <button
                        type="button"
                        onClick={() => onNavigateToSetupSection('horaires')}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Modifier les horaires
                      </button>
                    )}
                    {onNavigateToTab && (
                      <button
                        type="button"
                        onClick={() => onNavigateToTab('reservations-tables')}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Voir les réservations reçues
                      </button>
                    )}
                  </div>
                </div>

                <ConfigField
                  label="Durée moyenne d'occupation (minutes)"
                  hint="Ex. 120 minutes pour un service déjeuner. Un client arrivant à 12h00 libère la table vers 14h00."
                >
                  <input
                    type="number"
                    value={config.dureeTableMinutes}
                    onChange={(e) => handleConfigChange('dureeTableMinutes', parseInt(e.target.value, 10) || 120)}
                    className={inputClassName}
                    min="30"
                    max="480"
                    step="15"
                  />
                </ConfigField>

                <ConfigField
                  label="Couverts maximum par créneau"
                  hint="Nombre de personnes acceptées pour un même horaire d'arrivée (ex. 12h00)."
                >
                  <input
                    type="number"
                    value={config.maxTablesParJour}
                    onChange={(e) => handleConfigChange('maxTablesParJour', parseInt(e.target.value, 10) || 50)}
                    className={inputClassName}
                    min="1"
                    max="200"
                  />
                </ConfigField>

                <ConfigField
                  label="Délai minimum avant annulation (heures)"
                  hint={sharedFieldHint || 'Ex. 24 h avant la réservation.'}
                >
                  <input
                    type="number"
                    value={config.delaiAnnulationHeures}
                    onChange={(e) => handleConfigChange('delaiAnnulationHeures', parseInt(e.target.value, 10) || 24)}
                    className={inputClassName}
                    min="1"
                    max="168"
                  />
                </ConfigField>

                <div className="md:col-span-2 rounded-lg border border-rose-100 bg-rose-50/60 p-4">
                  <p className="text-sm font-medium text-rose-900">Validation par le restaurant</p>
                  <p className="mt-1 text-sm text-rose-800">
                    Chaque demande arrive en « à valider ». Acceptez ou refusez depuis l&apos;onglet{' '}
                    <strong>Réservations tables</strong>. Les clients connectés suivent le statut dans
                    « Mes commandes » ; les invités reçoivent un lien de suivi par email.
                  </p>
                </div>

                <div className="md:col-span-2 rounded-lg border border-violet-100 bg-violet-50/60 p-4 space-y-4">
                  <div>
                    <p className="text-sm font-medium text-violet-900">Arrhes à la réservation</p>
                    <p className="mt-1 text-sm text-violet-800">
                      Exigez un acompte en ligne avant d&apos;envoyer la demande au restaurant. Le
                      paiement en ligne doit être activé.
                    </p>
                  </div>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.arrhesTableActives}
                      onChange={(e) => handleConfigChange('arrhesTableActives', e.target.checked)}
                      className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Activer les arrhes sur les réservations table</span>
                  </label>

                  {config.arrhesTableActives && (
                    <>
                      <ConfigField
                        label="Mode de calcul"
                        hint="Montant fixe par réservation ou multiplié par le nombre de convives."
                      >
                        <select
                          value={config.arrhesTableMode}
                          onChange={(e) => handleConfigChange('arrhesTableMode', e.target.value)}
                          className={inputClassName}
                        >
                          <option value="FIXE">Montant fixe</option>
                          <option value="PAR_PERSONNE">Par personne</option>
                        </select>
                      </ConfigField>

                      <ConfigField
                        label={config.arrhesTableMode === 'PAR_PERSONNE' ? 'Montant par personne (€)' : 'Montant des arrhes (€)'}
                      >
                        <input
                          type="number"
                          value={config.arrhesTableMontant}
                          onChange={(e) =>
                            handleConfigChange('arrhesTableMontant', parseFloat(e.target.value) || 0)
                          }
                          className={inputClassName}
                          min="0"
                          step="0.5"
                        />
                      </ConfigField>

                      <div className="md:col-span-2 space-y-3 rounded-lg border border-violet-100 bg-white/80 p-4">
                        <label className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={config.arrhesTableExemptCompteVerifie}
                            onChange={(e) =>
                              handleConfigChange('arrhesTableExemptCompteVerifie', e.target.checked)
                            }
                            className="mt-1 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                          />
                          <span className="text-sm text-gray-700">
                            Exempter les clients avec compte Clickorico (sans arrhes)
                          </span>
                        </label>

                        {config.arrhesTableExemptCompteVerifie && (
                          <ConfigField
                            label="Seuil de commandes chez vous (optionnel)"
                            hint="Laissez vide ou 0 pour exempter tout client connecté. Sinon, nombre minimum de commandes livrées pour dispenser les arrhes."
                          >
                            <input
                              type="number"
                              value={config.arrhesTableSeuilExemptionCommandes ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value.trim();
                                handleConfigChange(
                                  'arrhesTableSeuilExemptionCommandes',
                                  raw === '' ? null : parseInt(raw, 10) || 0,
                                );
                              }}
                              className={inputClassName}
                              min="0"
                              step="1"
                              placeholder="Ex. 3 commandes"
                            />
                          </ConfigField>
                        )}
                      </div>

                      {!services.paiementEnLigne && (
                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          Activez le paiement en ligne dans les services pour encaisser les arrhes.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </ReservationConfigCard>
            )}

            {services.priseCreneaux && (
              <ReservationConfigCard icon="⏰" title="Prise de créneaux">
                <ConfigField
                  label="Pas horaire de la grille"
                  hint="Intervalle entre chaque créneau proposé au client."
                >
                  <select
                    value={config.pasGrilleMinutes}
                    onChange={(e) => handleConfigChange('pasGrilleMinutes', parseInt(e.target.value, 10))}
                    className={inputClassName}
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </ConfigField>

                <ConfigField
                  label="Durées proposées au client"
                  hint="Sélectionnez les durées que vos clients pourront réserver."
                >
                  <div className="flex flex-wrap gap-2">
                    {DUREE_PRESETS.map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => toggleDureeCreneau(m)}
                        className={`rounded-full border px-3 py-1 text-sm ${
                          config.dureesAutoriseesMinutes.includes(m)
                            ? 'border-indigo-600 bg-indigo-600 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        {m} min
                      </button>
                    ))}
                  </div>
                </ConfigField>

                <ConfigField
                  label="Délai minimum avant annulation (heures)"
                  hint={sharedFieldHint || 'Ex. 24 h avant le créneau.'}
                >
                  <input
                    type="number"
                    value={config.delaiAnnulationHeures}
                    onChange={(e) => handleConfigChange('delaiAnnulationHeures', parseInt(e.target.value, 10) || 24)}
                    className={inputClassName}
                    min="1"
                    max="168"
                  />
                </ConfigField>

                <div className="md:col-span-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={config.confirmationAutomatique}
                      onChange={(e) => handleConfigChange('confirmationAutomatique', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Confirmer automatiquement les réservations
                    </span>
                  </label>
                  {sharedFieldHint && (
                    <p className="mt-1 text-xs text-gray-500">{sharedFieldHint}</p>
                  )}
                </div>

                <div className="md:col-span-2 rounded-lg border border-indigo-100 bg-indigo-50/60 p-4">
                  <p className="text-sm font-medium text-indigo-900">Pour aller plus loin</p>
                  <p className="mt-1 text-sm text-indigo-800">
                    Ajoutez vos terrains ou salles, puis créez les prestations associées.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {onNavigateToTab && (
                      <>
                        <button
                          type="button"
                          onClick={() => navigateToTab('ressources')}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:text-indigo-900"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Gérer les ressources
                        </button>
                        <button
                          type="button"
                          onClick={() => navigateToTab('prestations')}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:text-indigo-900"
                        >
                          <ExternalLink className="h-4 w-4" />
                          Gérer les prestations
                        </button>
                      </>
                    )}
                    {!onNavigateToTab && (
                      <p className="text-xs text-indigo-700">
                        Utilisez les onglets Ressources et Prestations du menu pour compléter la configuration.
                      </p>
                    )}
                  </div>
                </div>
              </ReservationConfigCard>
            )}
          </div>
        </div>
      )}

      {hasChanges && (
        <div className="mt-6 flex items-center justify-end space-x-3 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleCancelChanges}
            className="flex items-center space-x-2 rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            <X className="h-4 w-4" />
            <span>Annuler</span>
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || validationErrors.length > 0}
            className="flex items-center space-x-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            <span>{loading ? 'Sauvegarde...' : 'Sauvegarder'}</span>
          </button>
        </div>
      )}
    </div>

    {typeof moyensDePaiementSlot === 'function' &&
      moyensDePaiementSlot({
        paiementEnLigne: services.paiementEnLigne === true,
        paiementSurPlace: services.paiementSurPlace === true,
        onToggleGlobal: handleServiceToggle,
        hasChanges,
        onSaveGlobals: handleSave,
        onCancelGlobals: handleCancelChanges,
        savingGlobals: loading,
        validationBlocked: validationErrors.length > 0,
      })}
    </>
  );
};

export default ServicesConfigurationPanel;
